import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Tooltip,
  Alert,
  Avatar,
  alpha,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LoginIcon from "@mui/icons-material/Login";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PeopleIcon from "@mui/icons-material/People";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import PageHeader from "../../common/PageHeader";

const ManageUsers = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);
  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [plans, setPlans] = useState([]);
  const [assignDialog, setAssignDialog] = useState({ open: false, user: null });
  const [assignPlan, setAssignPlan] = useState("");
  const [assignDays, setAssignDays] = useState(30);
  const [autoLoginDialog, setAutoLoginDialog] = useState({
    open: false,
    user: null,
    token: null,
    loading: false,
  });
  const limit = 15;

  useEffect(() => {
    loadUsers();
  }, [page, search]);
  useEffect(() => {
    loadPlans();
  }, []);

  const loadUsers = async () => {
    const res = await hitAxios({
      path: `/api/user/admin-get-all?page=${page}&limit=${limit}&search=${search}`,
      admin: true,
      showSnackbar: false,
    });
    if (res?.data?.success) {
      setUsers(res.data.data);
      setTotal(res.data.total);
    }
  };
  const loadPlans = async () => {
    const res = await hitAxios({
      path: "/api/plans/get-all",
      showSnackbar: false,
    });
    if (res?.data?.success) setPlans(res.data.data);
  };
  const toggleStatus = async (uid, currentStatus) => {
    await hitAxios({
      path: "/api/user/admin-update-status",
      post: true,
      admin: true,
      obj: { uid, status: currentStatus === "active" ? "banned" : "active" },
    });
    loadUsers();
  };
  const deleteUser = async (uid) => {
    if (!window.confirm(lang.deleteUserConfirm || "Delete this user?")) return;
    await hitAxios({
      path: "/api/user/admin-delete",
      post: true,
      admin: true,
      obj: { uid },
    });
    loadUsers();
  };
  const assignPlanToUser = async () => {
    await hitAxios({
      path: "/api/user/admin-assign-plan",
      post: true,
      admin: true,
      obj: {
        uid: assignDialog.user.uid,
        plan_id: assignPlan,
        duration_days: assignDays,
      },
    });
    setAssignDialog({ open: false, user: null });
    loadUsers();
  };
  const handleAutoLogin = async (user) => {
    setAutoLoginDialog({ open: true, user, token: null, loading: true });
    const res = await hitAxios({
      path: "/api/user/admin-autologin",
      post: true,
      admin: true,
      obj: { uid: user.uid },
      showSnackbar: false,
    });
    if (res?.data?.success)
      setAutoLoginDialog({
        open: true,
        user,
        token: res.data.token,
        loading: false,
      });
    else
      setAutoLoginDialog({
        open: false,
        user: null,
        token: null,
        loading: false,
      });
  };
  const openUserPanel = () => {
    const { token } = autoLoginDialog;
    if (!token) return;
    const tokenKey = process.env.REACT_APP_TOKEN + "_user";
    const newTab = window.open("about:blank", "_blank");
    newTab.document.write(
      `<script>localStorage.setItem("${tokenKey}", "${token}"); window.location.href = "/user";</script>`,
    );
  };

  return (
    <Box>
      <PageHeader
        title={lang.manageUsers || "Manage Users"}
        subtitle={`${total} total users registered`}
        icon={PeopleIcon}
      />

      <Card>
        <CardContent sx={{ p: 0 }}>
          {/* Search Bar */}
          <Box
            sx={{ p: 2.5, borderBottom: `1px solid ${theme.palette.divider}` }}
          >
            <TextField
              size="small"
              placeholder={lang.searchUsers || "Search by name or email..."}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <SearchIcon
                    sx={{ mr: 1, color: "text.secondary", fontSize: 18 }}
                  />
                ),
              }}
              sx={{ width: { xs: "100%", sm: 320 } }}
            />
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {[
                    "User",
                    "Plan",
                    "Expires",
                    "Status",
                    "Joined",
                    "Actions",
                  ].map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow
                    key={user.uid}
                    sx={{
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                      },
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          sx={{
                            width: 34,
                            height: 34,
                            bgcolor: theme.palette.primary.main,
                            fontSize: 13,
                            fontWeight: 700,
                          }}
                        >
                          {user.name?.[0]?.toUpperCase() || "U"}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {user.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.plan_name || "Free"}
                        size="small"
                        variant="outlined"
                        color={user.plan_name ? "primary" : "default"}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {user.plan_expires_at
                          ? new Date(user.plan_expires_at).toLocaleDateString()
                          : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.status}
                        size="small"
                        color={user.status === "active" ? "success" : "error"}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title={lang.loginAsUser || "Login as user"}>
                          <IconButton
                            size="small"
                            onClick={() => handleAutoLogin(user)}
                            disabled={user.status === "banned"}
                            sx={{
                              color: "primary.main",
                              "&:hover": {
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                              },
                            }}
                          >
                            <LoginIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={lang.assignPlan || "Assign Plan"}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setAssignDialog({ open: true, user })
                            }
                            sx={{
                              "&:hover": {
                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                              },
                            }}
                          >
                            <AssignmentIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            user.status === "active" ? "Ban User" : "Unban User"
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => toggleStatus(user.uid, user.status)}
                            sx={{
                              "&:hover": {
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                              },
                            }}
                          >
                            {user.status === "active" ? (
                              <BlockIcon
                                sx={{ fontSize: 16, color: "error.main" }}
                              />
                            ) : (
                              <CheckCircleIcon
                                sx={{ fontSize: 16, color: "success.main" }}
                              />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete User">
                          <IconButton
                            size="small"
                            onClick={() => deleteUser(user.uid)}
                            sx={{
                              "&:hover": {
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                              },
                            }}
                          >
                            <DeleteIcon
                              sx={{ fontSize: 16, color: "error.main" }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "center",
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Pagination
              count={Math.ceil(total / limit)}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
              shape="rounded"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Auto Login Dialog */}
      <Dialog
        open={autoLoginDialog.open}
        onClose={() =>
          setAutoLoginDialog({
            open: false,
            user: null,
            token: null,
            loading: false,
          })
        }
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LoginIcon sx={{ fontSize: 18, color: "primary.main" }} />
            </Box>
            <Box>
              <Typography fontWeight={700}>
                Login as {autoLoginDialog.user?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {autoLoginDialog.user?.email}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {autoLoginDialog.loading && (
            <Typography color="text.secondary" variant="body2" py={2}>
              Generating secure token...
            </Typography>
          )}
          {autoLoginDialog.token && (
            <Stack spacing={2} mt={1}>
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                Token expires in <strong>15 minutes</strong>. You'll be logged
                in as <strong>{autoLoginDialog.user?.email}</strong>.
              </Alert>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Your admin session stays active in this tab.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() =>
              setAutoLoginDialog({
                open: false,
                user: null,
                token: null,
                loading: false,
              })
            }
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<OpenInNewIcon />}
            onClick={openUserPanel}
            disabled={!autoLoginDialog.token}
            sx={{ borderRadius: 2 }}
          >
            Open User Panel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Plan Dialog */}
      <Dialog
        open={assignDialog.open}
        onClose={() => setAssignDialog({ open: false, user: null })}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={700}>
          Assign Plan to {assignDialog.user?.name}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Plan</InputLabel>
              <Select
                value={assignPlan}
                onChange={(e) => setAssignPlan(e.target.value)}
                label="Select Plan"
              >
                {plans.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name} — ${p.price}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Duration (days)"
              value={assignDays}
              onChange={(e) => setAssignDays(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setAssignDialog({ open: false, user: null })}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={assignPlanToUser}
            disabled={!assignPlan}
            sx={{ borderRadius: 2 }}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageUsers;
